export const GOVERNED_MEASURED_DIMENSIONS = [
  'operating_expense',
  'operating_income',
  'labor_cost',
  'assigned_people',
  // #5714 Child 1 — the M&A deal-underwriting and Finance-bridge dimensions the three-bucket
  // P&L taxonomy above cannot honestly express. None of these maps to 'other': 'other' is a
  // permanent catch-all mismatch in requested-measure admission (manager-requested-measure.ts)
  // that can never itself reach a non-'unverified' verdict, so a real measure with no home in
  // the P&L taxonomy needs its own named dimension, not a downgrade to the catch-all.
  'gross_synergy',
  'cost_to_achieve',
  'pre_tax_earnings_contribution',
  'after_tax_earnings_contribution',
  'income_tax',
  // #6178 — the margin-metric numerator dimensions the P&L taxonomy above
  // otherwise had no real home for, forcing them onto the 'other' catch-all
  // (which can never itself reach a non-'unverified' verdict) even though
  // they are fully governed, named quantities.
  'gross_profit',
  'ebitda',
  'other',
  'not_applicable',
] as const;

export const GOVERNED_MEASURE_VALUE_FORMS = [
  'amount',
  'ratio',
  'rate',
  'count',
  'change',
  'qualitative',
] as const;

export const GOVERNED_MEASURE_UNIT_FAMILIES = [
  'currency',
  'currency_per_person',
  'percent',
  'count',
  'time',
  'unitless',
  'other',
  'none',
] as const;

export const GOVERNED_MEASURE_NORMALIZATION_BASES = [
  'none',
  'fte_capacity',
  'reporting_headcount',
  'revenue',
  // #6178 — operating_margin_pct normalizes against gross profit, not
  // revenue; a real basis, not the 'other' catch-all.
  'gross_profit',
  'other',
] as const;

export type GovernedMeasuredDimension =
  (typeof GOVERNED_MEASURED_DIMENSIONS)[number];
export type GovernedMeasureValueForm =
  (typeof GOVERNED_MEASURE_VALUE_FORMS)[number];
export type GovernedMeasureUnitFamily =
  (typeof GOVERNED_MEASURE_UNIT_FAMILIES)[number];
export type GovernedMeasureNormalizationBasis =
  (typeof GOVERNED_MEASURE_NORMALIZATION_BASES)[number];

export type GovernedMeasureIdentity = Readonly<{
  measuredDimension: GovernedMeasuredDimension;
  valueForm: GovernedMeasureValueForm;
  unitFamily: GovernedMeasureUnitFamily;
  normalizationBasis: GovernedMeasureNormalizationBasis;
}>;

const GOVERNED_MEASURE_IDENTITY_FIELD_VOCABULARIES: Record<
  keyof GovernedMeasureIdentity,
  readonly string[]
> = {
  measuredDimension: GOVERNED_MEASURED_DIMENSIONS,
  valueForm: GOVERNED_MEASURE_VALUE_FORMS,
  unitFamily: GOVERNED_MEASURE_UNIT_FAMILIES,
  normalizationBasis: GOVERNED_MEASURE_NORMALIZATION_BASES,
};

// Only fields whose closed vocabulary actually admits `other` can ever carry
// it — `valueForm` has none (never did), so it always holds a real value and
// can never itself signal "unconstrained".
const GOVERNED_MEASURE_IDENTITY_OTHER_CAPABLE_FIELDS = (
  Object.keys(GOVERNED_MEASURE_IDENTITY_FIELD_VOCABULARIES) as (keyof GovernedMeasureIdentity)[]
).filter((field) => GOVERNED_MEASURE_IDENTITY_FIELD_VOCABULARIES[field].includes('other'));

/** The fields of a governed measure identity whose closed vocabulary admits
 *  `other` — used to derive total non-identification without hardcoding a
 *  field list next to the vocabularies it depends on. */
export function governedMeasureIdentityOtherCapableFields(): readonly (keyof GovernedMeasureIdentity)[] {
  return GOVERNED_MEASURE_IDENTITY_OTHER_CAPABLE_FIELDS;
}

/**
 * `other` on every field able to carry it is not a reader declining to
 * constrain SOME fields (requested-side wildcard semantics, #6178) — it is
 * total non-identification. Used by requested-measure admission
 * (manager-requested-measure.ts) to keep a wholly unidentified requested
 * identity from reading as a wildcard match for any fully declared selected
 * metric.
 */
export function governedMeasureIdentityIsUnconstrained(
  identity: GovernedMeasureIdentity,
): boolean {
  return GOVERNED_MEASURE_IDENTITY_OTHER_CAPABLE_FIELDS.every(
    (field) => identity[field] === 'other',
  );
}

export const OPERATING_INCOME_MEASURE_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'operating_income',
  valueForm: 'amount',
  unitFamily: 'currency',
  normalizationBasis: 'none',
};

export const OPERATING_EXPENSE_PER_HEAD_MEASURE_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'operating_expense',
  valueForm: 'ratio',
  unitFamily: 'currency_per_person',
  normalizationBasis: 'reporting_headcount',
};

// #5714 Child 1 — exact identity mapping from the amendment on #5714.
export const GROSS_SYNERGY_MEASURE_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'gross_synergy',
  valueForm: 'amount',
  unitFamily: 'currency',
  normalizationBasis: 'none',
};

export const COST_TO_ACHIEVE_MEASURE_IDENTITY: GovernedMeasureIdentity = {
  measuredDimension: 'cost_to_achieve',
  valueForm: 'amount',
  unitFamily: 'currency',
  normalizationBasis: 'none',
};
